/**
 * Semrush REST API Helpers (Story 20.4 AC #4/#5/#7/#12 + Story 20.5 AC #2-#7)
 *
 * One helper per endpoint group. Every helper:
 *
 *  1. Goes through `safeFetchSemrush` for retry + timeout + URL key-redaction.
 *  2. Sends a hard-coded `export_columns` array — NEVER an empty default.
 *     Default field-sets differ across endpoints (`domain_ranks` defaults to
 *     11 cols, `domain_organic` defaults to 19) — leaving the adapter without
 *     a stable contract about what record keys appear. AC #4 codifies this:
 *     the helpers throw `TypeError` synchronously if `exportColumns` is empty.
 *  3. Clamps `displayLimit` to 1000 rows max (AC #4 + Round 1 SRE finding).
 *  4. Parses the response via `parseSemrushCsv` with `expectedHeaders` =
 *     the same `exportColumns` we sent.
 *  5. NEVER logs the raw API key. The key flows through URL construction
 *     directly into `safeFetchSemrush` which redacts at the log boundary.
 *
 * **DIVERGENT FROM AHREFS (4 deliberate divergences — see semrush.ts JSDoc):**
 *  - Auth shape: `?key=<KEY>` query parameter, NOT `Authorization: Bearer`.
 *  - Response format: semicolon-delimited CSV, NOT JSON.
 *  - Unit cost: per-row OR fixed-per-request, NOT per row × field.
 *  - Persistence cap: 30-day Semrush ToS §3.3 enforced via cache.ts override.
 *
 * **Story 20.5 changelog (verified 2026-05-08 against developer.semrush.com):**
 *  - Adds 6 new endpoint helpers covering keyword-rank surface:
 *    - `domain_organic` — 12 default cols, 10u/row LIVE / 50u/row HISTORICAL
 *      (developer.semrush.com/api/v3/analytics/domain-reports/)
 *    - `domain_adwords` — 12 default cols + 4 ad-copy opt-ins, 20u/row LIVE
 *      / 100u/row HISTORICAL. Auto-sets `export_escape=1` when ad-copy
 *      fields present (legitimate copy may contain `;` separators).
 *    - `phrase_related` — 9 default cols, 40u/row
 *      (developer.semrush.com/api/v3/analytics/keyword-reports/)
 *    - `phrase_questions` — 9 default cols, 40u/row
 *    - `domain_rank_history` — 8 default cols, 50u/row HISTORICAL-ONLY.
 *      24-month range cap on monthly granularity, 31-day cap on daily.
 *    - `domain_organic_organic` — 7 default cols, 40u/row LIVE / 200u/row
 *      HISTORICAL. Conservative 25-row default-limit clamp because cost is
 *      4× domain_organic.
 *  - Reuses the existing `getSemrushCsv` + `parseSemrushCsv` primitives from
 *    Story 20.4 across the 6 new wrappers (same shape as Story 20.4 helpers).
 *    The 2 phrase wrappers share a private `fetchSemrushPhraseExpansion`
 *    helper (the only place generic-by-reportType made sense). The other 4
 *    wrappers each call `getSemrushCsv` directly — Task 1.2's "factor a
 *    generic" was reconsidered during implementation: the existing
 *    `getSemrushCsv` IS the shared primitive; an additional layer of
 *    abstraction would add types-noise without de-duplication payoff.
 *  - PII-safe `phraseHash`: structured-log events for `phrase_*` calls emit
 *    SHA-256(NFKC-normalized phrase) first 16 hex chars instead of the raw
 *    phrase string (Round 3 Red Team Attack (a) — keywords frequently contain
 *    PII / brand names / sensitive search queries; raw logging is a GDPR
 *    exposure path). Code Review Pass 1 strengthened from 12 to 16 hex chars
 *    (48 → 64 bits of entropy) to handle cross-tenant log-correlation
 *    collision rates and added Unicode normalization to the input.
 */

import { API_VERSIONS } from "../config";
import type { AdapterResult } from "../adapter";
import { adapterErr, adapterOk } from "../adapter";
import { parseSemrushCsv, type SemrushCsvResult } from "./csv";
import {
  parseSemrushApiError,
  semrushErrorTypeToAdapterCode,
  SemrushApiCallError,
} from "./errors";
import { safeFetchSemrush } from "./safe-fetch";
import { SEMRUSH_DISPLAY_LIMIT_MAX } from "./unit-budget";

// ============================================================================
// Constants
// ============================================================================

export const SEMRUSH_API_VERSION = API_VERSIONS.semrush;

/**
 * Analytics base URL — domain reports + keyword reports + most other
 * `?type=<report>` calls. Helpers append `?...` directly; do NOT include a
 * trailing slash.
 */
export const SEMRUSH_ANALYTICS_API_BASE = "https://api.semrush.com";

/**
 * Backlinks base URL — `backlinks_overview` and friends use a DIFFERENT
 * path prefix than Analytics (per developer.semrush.com/api/v3/analytics/
 * backlinks/ verified 2026-05-08).
 */
export const SEMRUSH_BACKLINKS_API_BASE = "https://api.semrush.com/analytics/v1";

/**
 * Trends base URL — Trends API endpoints (`/summary`, `/sources`,
 * `/destinations`, etc.) live under a separate path prefix and bill against
 * the SEPARATE Trends-quota subscription.
 */
export const SEMRUSH_TRENDS_API_BASE = "https://api.semrush.com/analytics/ta/api/v3";

/**
 * FREE balance endpoint per developer.semrush.com/api/get-started/
 * api-units-balance/ verified 2026-05-08. Returns the remaining unit
 * balance as a plain integer. Does NOT consume API units — chosen
 * specifically for connect-time validation + cron health-check.
 */
export const SEMRUSH_BALANCE_URL = "https://www.semrush.com/users/countapiunits.html";

// ============================================================================
// Invariant guards
// ============================================================================

/**
 * AC #4 + AC #12: every helper that builds a Semrush URL MUST pass a
 * non-empty `export_columns` array. Without explicit columns the API
 * returns the platform's documented "default columns" set, which differs
 * across endpoints — leaving the adapter without a stable record-shape
 * contract.
 *
 * Mirrors Ahrefs 20.1's `assertSelectNonEmpty` invariant.
 */
function assertExportColumnsNonEmpty(
  exportColumns: ReadonlyArray<string>,
  endpoint: string
): void {
  if (!Array.isArray(exportColumns) || exportColumns.length === 0) {
    throw new TypeError(
      `Semrush ${endpoint}: export_columns must be a non-empty array — sending without explicit columns leaves the records[] shape unstable.`
    );
  }
}

/**
 * AC #4 + Round 1 SRE finding: clamp `display_limit` to 1000 rows max on
 * widget-driven calls. Semrush's API allows up to 4,000,000 rows but a
 * sloppy widget config could burn 40K+ units in a single call. Throw
 * synchronously so the misconfiguration surfaces at dev time, not after
 * the customer's quota is gone.
 */
function assertDisplayLimitWithinCeiling(
  displayLimit: number | undefined,
  endpoint: string
): void {
  if (displayLimit === undefined) return;
  if (!Number.isInteger(displayLimit) || displayLimit < 1) {
    throw new TypeError(
      `Semrush ${endpoint}: display_limit must be a positive integer.`
    );
  }
  if (displayLimit > SEMRUSH_DISPLAY_LIMIT_MAX) {
    throw new TypeError(
      `Semrush ${endpoint}: display_limit must be <= ${SEMRUSH_DISPLAY_LIMIT_MAX} (got ${displayLimit}). Higher caps risk burning the customer's monthly quota in a single call.`
    );
  }
}

// ============================================================================
// URL builder
// ============================================================================

/**
 * Build a query-string URL with proper percent-encoding. Avoids ad-hoc
 * string concatenation — `URLSearchParams` handles encoding consistently.
 */
function buildUrl(base: string, params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    search.set(key, String(value));
  }
  return `${base}?${search.toString()}`;
}

// ============================================================================
// Response handler — common path
// ============================================================================

/**
 * Common GET-then-parse-CSV path. Used by every helper.
 *
 * Returns `AdapterResult<SemrushCsvResult>` so the adapter can use the
 * familiar success/error envelope.
 */
async function getSemrushCsv(
  url: string,
  apiKey: string,
  expectedHeaders: ReadonlyArray<string>
): Promise<AdapterResult<SemrushCsvResult>> {
  const fetchResult = await safeFetchSemrush(url, apiKey);
  if (!fetchResult.success) {
    return adapterErr(fetchResult.error);
  }
  const response = fetchResult.data;
  let body: string;
  try {
    body = await response.text();
  } catch (err) {
    return adapterErr({
      code: "invalid_response",
      message: `Failed to read Semrush response body: ${err instanceof Error ? err.message : String(err)}`,
      retryable: true,
    });
  }

  // **Code Review Pass 2 (High):** check for plaintext error PRESERVING the
  // real HTTP status. The previous implementation only ran the regex inside
  // `parseSemrushCsv` which hardcoded `status=200` — meaning a 5xx HTML
  // error page from Cloudflare/Semrush's edge would be misclassified as
  // `validation_error retryable=false`, blocking sync until next cron tick.
  // The 2-tier error parser handles HTTP-status fallback for non-2xx with
  // a non-CSV body.
  const errParsed = parseSemrushApiError(body, response.status, apiKey);
  if (errParsed) {
    return adapterErr({
      code: semrushErrorTypeToAdapterCode(errParsed.type),
      message: errParsed.message,
      retryable: errParsed.retryable,
      providerStatus: errParsed.status ?? undefined,
    });
  }

  // 2xx + body that isn't a numbered-error → parse as CSV.
  try {
    const csv = parseSemrushCsv(body, expectedHeaders);
    return adapterOk(csv);
  } catch (err) {
    if (err instanceof SemrushApiCallError) {
      return adapterErr({
        code: semrushErrorTypeToAdapterCode(err.type),
        message: err.message,
        retryable: err.retryable,
        providerStatus: err.status,
      });
    }
    return adapterErr({
      code: "invalid_response",
      message: `Semrush CSV parse failed: ${err instanceof Error ? err.message : String(err)}`,
      retryable: true,
    });
  }
}

// ============================================================================
// validateSemrushConnection (AC #2, #6)
// ============================================================================

export interface SemrushBalanceResult {
  /** Remaining API units on the customer's monthly Analytics plan. */
  readonly unitsRemaining: number;
}

/**
 * Validate a Semrush API key by calling `/users/countapiunits.html` (FREE
 * endpoint — does NOT consume API units per developer.semrush.com/api/
 * get-started/api-units-balance/). Used for:
 *
 *  - Connect-time validation (AC #2) — wrong-key check costs zero quota.
 *  - Cron health-check (AC #6) — runs every 15 min × N integrations; a paid
 *    health-check would burn the customer's monthly quota for nothing.
 *  - In-place key rotation (AC #15).
 *
 * Returns the parsed unit balance on success. The Trends-entitlement probe
 * is performed separately by the connect route (per AC #2) so this helper
 * stays simple + pure.
 */
export async function validateSemrushConnection(
  apiKey: string
): Promise<AdapterResult<SemrushBalanceResult>> {
  if (typeof apiKey !== "string" || apiKey.length === 0) {
    return adapterErr({
      code: "token_invalid",
      message: "Semrush API key is missing or empty.",
      retryable: false,
    });
  }
  const url = buildUrl(SEMRUSH_BALANCE_URL, { key: apiKey });
  const fetchResult = await safeFetchSemrush(url, apiKey);
  if (!fetchResult.success) {
    return adapterErr(fetchResult.error);
  }
  const response = fetchResult.data;
  let body: string;
  try {
    body = (await response.text()).trim();
  } catch (err) {
    return adapterErr({
      code: "invalid_response",
      message: `Failed to read Semrush balance response: ${err instanceof Error ? err.message : String(err)}`,
      retryable: true,
    });
  }

  // The balance endpoint returns plaintext errors too — same shape as data
  // endpoints (e.g. ERROR 50 :: API KEY ...).
  const errParsed = parseSemrushApiError(body, response.status, apiKey);
  if (errParsed) {
    return adapterErr({
      code: semrushErrorTypeToAdapterCode(errParsed.type),
      message: errParsed.message,
      retryable: errParsed.retryable,
      providerStatus: errParsed.status ?? undefined,
    });
  }

  // Successful response: a plain integer.
  const balance = Number.parseInt(body, 10);
  if (!Number.isFinite(balance) || balance < 0) {
    return adapterErr({
      code: "invalid_response",
      message: `Semrush balance endpoint returned non-integer body: "${body.slice(0, 80)}"`,
      retryable: true,
    });
  }
  return adapterOk({ unitsRemaining: balance });
}

// ============================================================================
// Endpoint Group A: domain_ranks (AC #4)
// ============================================================================

export interface SemrushDomainRanksParams {
  readonly apiKey: string;
  readonly target: string;
  readonly database: string;
  readonly exportColumns: ReadonlyArray<string>;
  readonly displayDate?: string; // YYYYMM15 for historical
  readonly displayLimit?: number;
}

export interface SemrushHelperResult {
  readonly result: SemrushCsvResult;
  /** Estimated unit cost for the call (server-side cost may vary). */
  readonly estimatedUnits: number;
}

/**
 * `GET /?type=domain_ranks` — Domain Overview, single-database.
 *
 * Cost: 10 units/row LIVE, 50 units/row HISTORICAL (`display_date` set) per
 * developer.semrush.com/api/v3/analytics/overview-reports/ 2026-05-08.
 */
export async function fetchSemrushDomainRanks(
  params: SemrushDomainRanksParams
): Promise<AdapterResult<SemrushHelperResult>> {
  assertExportColumnsNonEmpty(params.exportColumns, "/domain_ranks");
  assertDisplayLimitWithinCeiling(params.displayLimit, "/domain_ranks");
  const url = buildUrl(SEMRUSH_ANALYTICS_API_BASE + "/", {
    type: "domain_ranks",
    key: params.apiKey,
    domain: params.target,
    database: params.database,
    export_columns: params.exportColumns.join(","),
    export_decode: 1,
    display_date: params.displayDate,
    display_limit: params.displayLimit,
  });
  const csv = await getSemrushCsv(url, params.apiKey, params.exportColumns);
  if (!csv.success) return csv;
  const isHistorical = params.displayDate !== undefined;
  const perRow = isHistorical ? 50 : 10;
  return adapterOk({
    result: csv.data,
    estimatedUnits: perRow * csv.data.rows.length,
  });
}

// ============================================================================
// Endpoint Group B: backlinks_overview (AC #4)
// ============================================================================

export interface SemrushBacklinksOverviewParams {
  readonly apiKey: string;
  readonly target: string;
  readonly targetType: "root_domain" | "domain" | "url";
  readonly exportColumns: ReadonlyArray<string>;
}

/**
 * `GET /analytics/v1/?type=backlinks_overview` — single-row backlinks
 * summary. **Cost: 40 units FIXED PER REQUEST** (NOT per row) per
 * developer.semrush.com/api/v3/analytics/backlinks/ 2026-05-08.
 */
export async function fetchSemrushBacklinksOverview(
  params: SemrushBacklinksOverviewParams
): Promise<AdapterResult<SemrushHelperResult>> {
  assertExportColumnsNonEmpty(params.exportColumns, "/backlinks_overview");
  const url = buildUrl(SEMRUSH_BACKLINKS_API_BASE + "/", {
    type: "backlinks_overview",
    key: params.apiKey,
    target: params.target,
    target_type: params.targetType,
    export_columns: params.exportColumns.join(","),
    export_decode: 1,
  });
  const csv = await getSemrushCsv(url, params.apiKey, params.exportColumns);
  if (!csv.success) return csv;
  return adapterOk({
    result: csv.data,
    // 40u is FIXED-per-request regardless of whether 0 or 1 rows came back —
    // Semrush bills the call shape, not the row count. If no rows came back
    // (empty workspace) the customer was still billed.
    estimatedUnits: 40,
  });
}

// ============================================================================
// Endpoint Group C: phrase_kdi (AC #4)
// ============================================================================

export interface SemrushPhraseKdiParams {
  readonly apiKey: string;
  readonly phrases: ReadonlyArray<string>;
  readonly database: string;
  readonly exportColumns: ReadonlyArray<string>;
  /**
   * **AC #4 list-style invariant:** mandatory on `phrase_kdi` so the helper
   * clamp at `SEMRUSH_DISPLAY_LIMIT_MAX` (1000) actually triggers. Defaults
   * to `phrases.length` when omitted (one row per requested keyword).
   */
  readonly displayLimit?: number;
}

/**
 * `GET /?type=phrase_kdi` — Keyword Difficulty Index, batched.
 *
 * Accepts 1-100 keywords semicolon-delimited per
 * developer.semrush.com/api/v3/analytics/keyword-reports/ 2026-05-08.
 *
 * Cost: 50 units/row.
 */
export async function fetchSemrushPhraseKdi(
  params: SemrushPhraseKdiParams
): Promise<AdapterResult<SemrushHelperResult>> {
  assertExportColumnsNonEmpty(params.exportColumns, "/phrase_kdi");
  if (!Array.isArray(params.phrases) || params.phrases.length === 0) {
    throw new TypeError(
      "Semrush /phrase_kdi: phrases must be a non-empty array (1-100 keywords)."
    );
  }
  if (params.phrases.length > 100) {
    throw new TypeError(
      `Semrush /phrase_kdi: phrases.length must be <= 100 (got ${params.phrases.length}). Split into batches of 100.`
    );
  }
  // **AC #4 list-style invariant** — clamp `display_limit` to the 1000-row
  // ceiling. Defaults to `phrases.length` (one row per requested keyword;
  // never returns more than that). Throws synchronously on values >1000.
  const displayLimit = params.displayLimit ?? params.phrases.length;
  assertDisplayLimitWithinCeiling(displayLimit, "/phrase_kdi");
  const url = buildUrl(SEMRUSH_ANALYTICS_API_BASE + "/", {
    type: "phrase_kdi",
    key: params.apiKey,
    // Semrush expects semicolons between phrases — semicolons inside a
    // phrase value are pathological (and not valid keywords); upstream
    // callers should reject such phrases at validation time.
    phrase: params.phrases.join(";"),
    database: params.database,
    export_columns: params.exportColumns.join(","),
    export_decode: 1,
    display_limit: displayLimit,
  });
  const csv = await getSemrushCsv(url, params.apiKey, params.exportColumns);
  if (!csv.success) return csv;
  return adapterOk({
    result: csv.data,
    estimatedUnits: 50 * csv.data.rows.length,
  });
}

// ============================================================================
// Endpoint Group D: Trends /summary (AC #4)
// ============================================================================

export interface SemrushTrendsSummaryParams {
  readonly apiKey: string;
  /** 1-200 target domains. */
  readonly targets: ReadonlyArray<string>;
  /** YYYY-MM-01 (Trends API requires first-of-month). */
  readonly displayDate: string;
  readonly country?: string;
  readonly deviceType?: "desktop" | "mobile" | "all";
  readonly exportColumns: ReadonlyArray<string>;
  /**
   * Optional row cap. The Trends-entitlement probe at connect-time uses
   * `displayLimit: 1` to keep the cost at exactly 1 unit per the spec
   * (AC #2). Clamped to `SEMRUSH_DISPLAY_LIMIT_MAX` (1000) at the helper.
   */
  readonly displayLimit?: number;
}

const TRENDS_DISPLAY_DATE_REGEX = /^\d{4}-\d{2}-01$/;

/**
 * `GET /analytics/ta/api/v3/summary` — Trends summary endpoint.
 *
 * Carries the 2026-flagship AI-visibility columns: `ai_assistants`,
 * `ai_search`, `unknown_channel`. Per developer.semrush.com/api/v3/trends/
 * api-reference/ verified 2026-05-08.
 *
 * **Cost: 1 unit/row from the SEPARATE Trends quota** (10K Trends units/
 * month for the basic tier per semrush.com/kb/930-trends-api). The caller
 * MUST increment `semrush:trends_units_used:*` (NOT `semrush:units_used:*`).
 */
export async function fetchSemrushTrendsSummary(
  params: SemrushTrendsSummaryParams
): Promise<AdapterResult<SemrushHelperResult>> {
  assertExportColumnsNonEmpty(params.exportColumns, "/trends/summary");
  if (!Array.isArray(params.targets) || params.targets.length === 0) {
    throw new TypeError(
      "Semrush /trends/summary: targets must be a non-empty array (1-200 domains)."
    );
  }
  if (params.targets.length > 200) {
    throw new TypeError(
      `Semrush /trends/summary: targets.length must be <= 200 (got ${params.targets.length}).`
    );
  }
  if (!TRENDS_DISPLAY_DATE_REGEX.test(params.displayDate)) {
    throw new TypeError(
      `Semrush /trends/summary: displayDate must be YYYY-MM-01 (got "${params.displayDate}").`
    );
  }
  if (params.displayLimit !== undefined) {
    assertDisplayLimitWithinCeiling(params.displayLimit, "/trends/summary");
  }
  const url = buildUrl(SEMRUSH_TRENDS_API_BASE + "/summary", {
    key: params.apiKey,
    targets: params.targets.join(","),
    display_date: params.displayDate,
    country: params.country,
    device_type: params.deviceType,
    export_columns: params.exportColumns.join(","),
    export_decode: 1,
    display_limit: params.displayLimit,
  });
  const csv = await getSemrushCsv(url, params.apiKey, params.exportColumns);
  if (!csv.success) return csv;
  return adapterOk({
    result: csv.data,
    estimatedUnits: csv.data.rows.length, // 1 Trends-unit per row
  });
}

// ============================================================================
// Story 20.5 — Database regex invariant (AC #2 / Round 3 Red Team Attack (d))
// ============================================================================

/**
 * Per AC #2 / AC #8: every keyword-rank endpoint requires an explicit 2-char
 * lowercase regional database. Without it, Semrush returns rows for ALL ~30
 * regional databases producing 30× the expected unit cost. The helper-level
 * synchronous throw catches widget-config bugs at dev time, before they hit
 * the network.
 */
const SEMRUSH_DATABASE_REGEX = /^[a-z]{2}$/;

function assertDatabase(database: string, endpoint: string): void {
  if (typeof database !== "string" || !SEMRUSH_DATABASE_REGEX.test(database)) {
    throw new TypeError(
      `Semrush ${endpoint}: database must match /^[a-z]{2}$/ (got "${database}"). Without an explicit database Semrush returns ALL ~30 regional databases producing 30× cost.`
    );
  }
}

/**
 * Per AC #5 — `domain_rank_history` is historical-only; the `display_date`
 * is mandatory and must be `YYYYMM15` format. Synchronous throw at the
 * helper boundary catches misuse before the API call burns units.
 *
 * **Round 3 CR MEDIUM #2:** strengthened to bound year ∈ [2010, 2199] and
 * month ∈ [01, 12], matching the route-level Zod schema. Direct helper
 * callers (no current internal callers, but contract is for direct use)
 * previously accepted `"00001315"`, `"99999915"`, etc. — values that
 * Semrush rejects opaquely while burning 50-100u/row of a HISTORICAL
 * URL Reports call before the rejection lands.
 */
const SEMRUSH_YYYYMM15_REGEX = /^(20[1-9]\d|21\d\d)(0[1-9]|1[0-2])15$/;

function assertYyyyMm15(value: string, endpoint: string): void {
  if (typeof value !== "string" || !SEMRUSH_YYYYMM15_REGEX.test(value)) {
    throw new TypeError(
      `Semrush ${endpoint}: displayDate must be YYYYMM15 (got "${value}").`
    );
  }
}

/**
 * Story 35.1 — `targetType` parameter validation for backlinks endpoints
 * (AC #2-#4, AC #13, AC #15). The Backlinks API rejects unknown values with
 * `ERROR 50`. Synchronous throw at the helper boundary catches widget-config
 * bugs at dev time, before they hit the network.
 */
const SEMRUSH_BACKLINKS_TARGET_TYPES: ReadonlySet<string> = new Set([
  "root_domain",
  "domain",
  "url",
]);

function assertTargetType(targetType: string, endpoint: string): void {
  if (
    typeof targetType !== "string" ||
    !SEMRUSH_BACKLINKS_TARGET_TYPES.has(targetType)
  ) {
    throw new TypeError(
      `Semrush ${endpoint}: targetType must be one of root_domain, domain, url (got "${targetType}"). Semrush Backlinks API rejects other values with ERROR 50.`
    );
  }
}

/**
 * Per AC #4 — `phrase` parameter validation (1-200 chars, non-whitespace).
 * Semrush rejects longer phrases as `ERROR 50`; whitespace-only phrases
 * waste units returning empty results. Throw at helper boundary.
 */
function assertPhrase(phrase: string, endpoint: string): void {
  if (typeof phrase !== "string") {
    throw new TypeError(`Semrush ${endpoint}: phrase must be a string.`);
  }
  const trimmed = phrase.trim();
  if (trimmed.length === 0) {
    throw new TypeError(`Semrush ${endpoint}: phrase must be non-empty.`);
  }
  if (trimmed.length > 200) {
    throw new TypeError(
      `Semrush ${endpoint}: phrase must be <= 200 chars (got ${trimmed.length}). Semrush rejects longer phrases as ERROR 50.`
    );
  }
}

// ============================================================================
// Story 20.5 — PII-safe phrase hash (AC #4 / Round 3 Red Team Attack (a))
// ============================================================================

/**
 * Number of hex characters of the SHA-256 prefix to emit as the phrase hash.
 * 16 hex chars = 64 bits of entropy → birthday-collision after ~4 billion
 * inputs (vs 16M at 12 hex chars / 48 bits). Code Review Pass 1 flagged the
 * 12-hex variant as collision-prone for cross-tenant log analysis.
 */
const SEMRUSH_PHRASE_HASH_HEX_LEN = 16;

/**
 * Compute a stable short hash of a customer keyword for log correlation
 * WITHOUT exposing the raw phrase.
 *
 * Customer keywords frequently contain PII, brand names, lawsuit queries,
 * pasted email addresses, etc. Raw-keyword logging would create a GDPR
 * exposure path through worker logs / Cloudflare Workers metrics / our own
 * structured-event sinks.
 *
 * **Normalization (Code Review Pass 1):** the input phrase is NFKC-normalized,
 * lowercased, trimmed, and inner whitespace collapsed before hashing. Two
 * formattings of the same logical query (`"SEO Tools"`, `"seo tools"`,
 * `"  seo  tools  "`) produce the same hash, so log-correlation across worker
 * invocations stays useful.
 *
 * Returns the first 16 hex chars of `SHA-256(normalized)` — 64 bits of
 * entropy. Used in the `semrush_api_call_units` event field for `phrase_*`
 * calls only. Never logs the raw phrase.
 */
export async function computeSemrushPhraseHash(phrase: string): Promise<string> {
  const normalized = phrase
    .normalize("NFKC")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
  const bytes = new TextEncoder().encode(normalized);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  const view = new Uint8Array(digest);
  let hex = "";
  const byteCount = SEMRUSH_PHRASE_HASH_HEX_LEN / 2;
  for (let i = 0; i < byteCount; i++) {
    hex += view[i]!.toString(16).padStart(2, "0");
  }
  return hex;
}

// ============================================================================
// Story 20.5 — generic endpoint helper (Task 1.2)
// ============================================================================

/**
 * Conservative-default clamp for `domain_organic_organic` per AC #6:
 * widget-config without explicit `displayLimit` → 25 rows (NOT 1000)
 * because the endpoint costs 4× `domain_organic` (200u/row historical).
 */
export const SEMRUSH_DOMAIN_ORGANIC_COMPETITORS_DEFAULT_LIMIT = 25;

/**
 * Maximum row count for `domain_rank_history` monthly granularity (24
 * months) per AC #5. Daily granularity caps at 31 (Semrush only returns
 * the last 31 daily rows regardless).
 */
export const SEMRUSH_DOMAIN_RANK_HISTORY_MAX_MONTHS = 24;
export const SEMRUSH_DOMAIN_RANK_HISTORY_MAX_DAYS = 31;

// ============================================================================
// Story 20.5 — domain_organic helper (AC #2)
// ============================================================================

export interface SemrushDomainOrganicParams {
  readonly apiKey: string;
  readonly target: string;
  readonly database: string;
  readonly exportColumns: ReadonlyArray<string>;
  readonly displayLimit?: number;
  readonly displayOffset?: number;
  readonly displayDate?: string; // YYYYMM15 for historical
  readonly displaySort?: string;
  readonly displayFilter?: string;
  readonly positionsType?: "all" | "organic" | "noweird";
}

/**
 * `domain_organic` default `export_columns` (12 fields) per AC #2.
 *
 * **Note:** the catalogue lists `Nr` (number-of-SERP-results) but it is NOT
 * in the default — `Nr` is rarely-rendered and adds 1 unit/row × 1000-row ×
 * 24h-cache cycles. Available as opt-in via widget config in Story 20.6.
 */
export const SEMRUSH_DOMAIN_ORGANIC_DEFAULT_COLUMNS: ReadonlyArray<string> = [
  "Ph", "Po", "Pp", "Pd", "Nq", "Cp", "Ur", "Tr", "Tc", "Co", "Td", "Fk",
];

/**
 * `GET /?type=domain_organic` — every keyword the customer's domain ranks
 * for organically, with position deltas, search volume, CPC, traffic share,
 * and SERP feature codes (including the 2026 AI-Overview signal).
 *
 * Cost: `displayDate ? 50 * rows : 10 * rows` per
 * developer.semrush.com/api/v3/analytics/domain-reports/ verified 2026-05-08.
 *
 * TODO(20.6): fixture mode short-circuit — when the future `loadSemrushFixture`
 * helper lands in Story 20.6, intercept here BEFORE `safeFetchSemrush` (mirrors
 * Ahrefs `loadAhrefsFixture` shipped in Story 20.3). Fixture mode runs in
 * `fetchMetrics` BEFORE any `apiKey` validation.
 */
export async function fetchSemrushDomainOrganic(
  params: SemrushDomainOrganicParams
): Promise<AdapterResult<SemrushHelperResult>> {
  assertExportColumnsNonEmpty(params.exportColumns, "/domain_organic");
  assertDisplayLimitWithinCeiling(params.displayLimit, "/domain_organic");
  assertDatabase(params.database, "/domain_organic");
  const url = buildUrl(SEMRUSH_ANALYTICS_API_BASE + "/", {
    type: "domain_organic",
    key: params.apiKey,
    domain: params.target,
    database: params.database,
    export_columns: params.exportColumns.join(","),
    export_decode: 1,
    display_limit: params.displayLimit,
    display_offset: params.displayOffset,
    display_date: params.displayDate,
    display_sort: params.displaySort,
    display_filter: params.displayFilter,
    positions_type: params.positionsType,
  });
  const csv = await getSemrushCsv(url, params.apiKey, params.exportColumns);
  if (!csv.success) return csv;
  const perRow = params.displayDate ? 50 : 10;
  return adapterOk({
    result: csv.data,
    estimatedUnits: perRow * csv.data.rows.length,
  });
}

// ============================================================================
// Story 20.5 — domain_adwords helper (AC #3)
// ============================================================================

export interface SemrushDomainAdwordsParams {
  readonly apiKey: string;
  readonly target: string;
  readonly database: string;
  readonly exportColumns: ReadonlyArray<string>;
  readonly displayLimit?: number;
  readonly displayOffset?: number;
  readonly displayDate?: string;
  readonly displaySort?: string;
  readonly displayFilter?: string;
}

/** `domain_adwords` default columns (12 fields) per AC #3. */
export const SEMRUSH_DOMAIN_ADWORDS_DEFAULT_COLUMNS: ReadonlyArray<string> = [
  "Ph", "Po", "Pp", "Pd", "Ab", "Nq", "Cp", "Tr", "Tc", "Co", "Tt", "Ds",
];

/** Ad-copy fields that require `export_escape=1` when present (semicolon-safe). */
const SEMRUSH_DOMAIN_ADWORDS_AD_COPY_FIELDS: ReadonlySet<string> = new Set([
  "Tt", "Ds", "Vu", "Ur",
]);

/**
 * `GET /?type=domain_adwords` — every paid-search keyword the customer's
 * domain bids on, with ad-copy + position-block + position-delta data.
 *
 * Cost: `displayDate ? 100 * rows : 20 * rows`.
 *
 * Auto-sets `export_escape=1` when ANY of `Tt`/`Ds`/`Vu`/`Ur` appears in
 * `exportColumns` because legitimate ad copy may contain semicolons (e.g.
 * "Save 20%; Free shipping") which would otherwise corrupt the CSV.
 *
 * TODO(20.6): fixture mode short-circuit — see `fetchSemrushDomainOrganic`.
 */
export async function fetchSemrushDomainAdwords(
  params: SemrushDomainAdwordsParams
): Promise<AdapterResult<SemrushHelperResult>> {
  assertExportColumnsNonEmpty(params.exportColumns, "/domain_adwords");
  assertDisplayLimitWithinCeiling(params.displayLimit, "/domain_adwords");
  assertDatabase(params.database, "/domain_adwords");
  const needsEscape = params.exportColumns.some((c) =>
    SEMRUSH_DOMAIN_ADWORDS_AD_COPY_FIELDS.has(c)
  );
  const url = buildUrl(SEMRUSH_ANALYTICS_API_BASE + "/", {
    type: "domain_adwords",
    key: params.apiKey,
    domain: params.target,
    database: params.database,
    export_columns: params.exportColumns.join(","),
    export_decode: 1,
    export_escape: needsEscape ? 1 : undefined,
    display_limit: params.displayLimit,
    display_offset: params.displayOffset,
    display_date: params.displayDate,
    display_sort: params.displaySort,
    display_filter: params.displayFilter,
  });
  const csv = await getSemrushCsv(url, params.apiKey, params.exportColumns);
  if (!csv.success) return csv;
  const perRow = params.displayDate ? 100 : 20;
  return adapterOk({
    result: csv.data,
    estimatedUnits: perRow * csv.data.rows.length,
  });
}

// ============================================================================
// Story 20.5 — phrase_related + phrase_questions helpers (AC #4)
// ============================================================================

export interface SemrushPhraseExpansionParams {
  readonly apiKey: string;
  readonly phrase: string;
  readonly database: string;
  readonly exportColumns: ReadonlyArray<string>;
  readonly displayLimit?: number;
  readonly displayOffset?: number;
  readonly displaySort?: string;
  readonly displayFilter?: string;
}

/** Shared default columns (9 fields) for both phrase expansion endpoints. */
export const SEMRUSH_PHRASE_EXPANSION_DEFAULT_COLUMNS: ReadonlyArray<string> = [
  "Ph", "Nq", "Cp", "Co", "Nr", "Td", "Fk", "In", "Kd",
];

async function fetchSemrushPhraseExpansion(
  reportType: "phrase_related" | "phrase_questions",
  params: SemrushPhraseExpansionParams
): Promise<AdapterResult<SemrushHelperResult>> {
  const endpoint = `/${reportType}`;
  assertExportColumnsNonEmpty(params.exportColumns, endpoint);
  assertDisplayLimitWithinCeiling(params.displayLimit, endpoint);
  assertDatabase(params.database, endpoint);
  assertPhrase(params.phrase, endpoint);
  const url = buildUrl(SEMRUSH_ANALYTICS_API_BASE + "/", {
    type: reportType,
    key: params.apiKey,
    phrase: params.phrase,
    database: params.database,
    export_columns: params.exportColumns.join(","),
    export_decode: 1,
    display_limit: params.displayLimit,
    display_offset: params.displayOffset,
    display_sort: params.displaySort,
    display_filter: params.displayFilter,
  });
  const csv = await getSemrushCsv(url, params.apiKey, params.exportColumns);
  if (!csv.success) return csv;
  return adapterOk({
    result: csv.data,
    estimatedUnits: 40 * csv.data.rows.length,
  });
}

/**
 * `GET /?type=phrase_related` — keyword-discovery / variations / "keywords
 * that share words with this seed". Powers the "what should we rank for next
 * quarter?" widget. Cost: 40u/row.
 *
 * TODO(20.6): fixture mode short-circuit — see `fetchSemrushDomainOrganic`.
 */
export function fetchSemrushPhraseRelated(
  params: SemrushPhraseExpansionParams
): Promise<AdapterResult<SemrushHelperResult>> {
  return fetchSemrushPhraseExpansion("phrase_related", params);
}

/**
 * `GET /?type=phrase_questions` — question-form keywords ("how", "why",
 * "best", etc.). Powers the "AI-friendly questions to target for AI Overview
 * presence" widget — 2026 SEO heavily favors question-form long-tails since
 * AI Overviews favor answer-style content. Cost: 40u/row.
 *
 * TODO(20.6): fixture mode short-circuit — see `fetchSemrushDomainOrganic`.
 */
export function fetchSemrushPhraseQuestions(
  params: SemrushPhraseExpansionParams
): Promise<AdapterResult<SemrushHelperResult>> {
  return fetchSemrushPhraseExpansion("phrase_questions", params);
}

// ============================================================================
// Story 20.5 — domain_rank_history helper (AC #5)
// ============================================================================

export interface SemrushDomainRankHistoryParams {
  readonly apiKey: string;
  readonly target: string;
  readonly database: string;
  readonly exportColumns: ReadonlyArray<string>;
  readonly displayLimit?: number;
  readonly displayOffset?: number;
  readonly displayDate: string; // mandatory YYYYMM15 — historical-only endpoint
  readonly displayDaily?: 0 | 1;
}

/** `domain_rank_history` default columns (8 fields) per AC #5. */
export const SEMRUSH_DOMAIN_RANK_HISTORY_DEFAULT_COLUMNS: ReadonlyArray<string> = [
  "Dt", "Rk", "Or", "Ot", "Oc", "Ad", "At", "Ac",
];

export interface SemrushHelperResultWithWarnings extends SemrushHelperResult {
  readonly warnings?: ReadonlyArray<{
    readonly reportType: string;
    readonly reason: string;
  }>;
}

/**
 * `GET /?type=domain_rank_history` — monthly Semrush Rank + organic/paid-
 * keyword counts + traffic over the past N months. Powers the "Authority
 * over Time" + "Organic Keywords trend" line-chart widgets.
 *
 * **Historical-only** — `displayDate` is mandatory (Semrush rejects calls
 * without it).
 *
 * **24-month range cap (AC #5 / Round 4 ADR-4):** for monthly granularity
 * (`displayDaily: 0`), `displayLimit` is internally clamped to
 * `min(input, 24)`. For daily granularity (`displayDaily: 1`), clamped to
 * `min(input, 31)` (Semrush returns at most 31 daily rows). Beyond 24 months
 * produces 240 rows × 50u = 12K units per call; at the 200K/day per-tenant
 * budget that's 16 calls/day before circuit-breaker fires.
 *
 * Cost: 50u/row (historical-only — no live mode).
 *
 * TODO(20.6): fixture mode short-circuit — see `fetchSemrushDomainOrganic`.
 */
export async function fetchSemrushDomainRankHistory(
  params: SemrushDomainRankHistoryParams
): Promise<AdapterResult<SemrushHelperResultWithWarnings>> {
  assertExportColumnsNonEmpty(params.exportColumns, "/domain_rank_history");
  assertDatabase(params.database, "/domain_rank_history");
  assertYyyyMm15(params.displayDate, "/domain_rank_history");

  const warnings: Array<{ reportType: string; reason: string }> = [];

  // Range-cap clamp (AC #5 / Round 4 ADR-4). The helper does NOT throw on
  // over-clamp — it silently caps and emits a warning so the widget surfaces
  // the boundary. **Code Review Pass 1:** when `displayLimit` is undefined,
  // we default to the per-granularity max (24 months / 31 days) so the
  // helper produces a deterministic URL + cache key and the Semrush implicit
  // default never sneaks through.
  const isDaily = params.displayDaily === 1;
  const granularityMax = isDaily
    ? SEMRUSH_DOMAIN_RANK_HISTORY_MAX_DAYS
    : SEMRUSH_DOMAIN_RANK_HISTORY_MAX_MONTHS;
  let effectiveLimit: number;
  if (params.displayLimit === undefined) {
    effectiveLimit = granularityMax;
  } else {
    assertDisplayLimitWithinCeiling(params.displayLimit, "/domain_rank_history");
    if (params.displayLimit > granularityMax) {
      effectiveLimit = granularityMax;
      warnings.push({
        reportType: "domain-rank-history",
        reason: isDaily
          ? "date_range_clamped_to_31_days"
          : "date_range_clamped_to_24_months",
      });
    } else {
      effectiveLimit = params.displayLimit;
    }
  }

  const url = buildUrl(SEMRUSH_ANALYTICS_API_BASE + "/", {
    type: "domain_rank_history",
    key: params.apiKey,
    domain: params.target,
    database: params.database,
    export_columns: params.exportColumns.join(","),
    export_decode: 1,
    display_limit: effectiveLimit,
    display_offset: params.displayOffset,
    display_date: params.displayDate,
    display_daily: params.displayDaily,
  });
  const csv = await getSemrushCsv(url, params.apiKey, params.exportColumns);
  if (!csv.success) return csv;
  return adapterOk({
    result: csv.data,
    estimatedUnits: 50 * csv.data.rows.length,
    ...(warnings.length > 0 ? { warnings } : {}),
  });
}

// ============================================================================
// Story 20.5 — domain_organic_organic helper (AC #6)
// ============================================================================

export interface SemrushDomainOrganicCompetitorsParams {
  readonly apiKey: string;
  readonly target: string;
  readonly database: string;
  readonly exportColumns: ReadonlyArray<string>;
  readonly displayLimit?: number;
  readonly displayOffset?: number;
  readonly displayDate?: string;
  readonly displaySort?: string;
}

/** `domain_organic_organic` default columns (7 fields) per AC #6. */
export const SEMRUSH_DOMAIN_ORGANIC_COMPETITORS_DEFAULT_COLUMNS: ReadonlyArray<string> = [
  "Dn", "Cr", "Np", "Or", "Ot", "Oc", "Ad",
];

/**
 * `GET /?type=domain_organic_organic` — competitors in organic search
 * (domains ranking for the same keywords as my client). Powers the "Top
 * Organic Competitors" data-table widget.
 *
 * Cost: `displayDate ? 200 * rows : 40 * rows`. Note: this is 4× the cost
 * of `domain_organic` — burning 1000 rows historical = 200K units, exactly
 * the per-tenant daily budget. The widget config UI MUST default this widget
 * to `displayLimit: 10` and explicitly warn if customer raises it above 25.
 *
 * **Conservative-default clamp (AC #6):** when no explicit `displayLimit`
 * is supplied, defaults to 25 rows (NOT 1000) because cost is 4× domain_organic.
 *
 * TODO(20.6): fixture mode short-circuit — see `fetchSemrushDomainOrganic`.
 */
export async function fetchSemrushDomainOrganicOrganic(
  params: SemrushDomainOrganicCompetitorsParams
): Promise<AdapterResult<SemrushHelperResult>> {
  assertExportColumnsNonEmpty(params.exportColumns, "/domain_organic_organic");
  assertDatabase(params.database, "/domain_organic_organic");
  // Cost is 4× domain_organic so 25 is the safe default; widget config raises
  // explicitly. This applies BEFORE the 1000-row ceiling check.
  const effectiveLimit =
    params.displayLimit ?? SEMRUSH_DOMAIN_ORGANIC_COMPETITORS_DEFAULT_LIMIT;
  assertDisplayLimitWithinCeiling(effectiveLimit, "/domain_organic_organic");

  const url = buildUrl(SEMRUSH_ANALYTICS_API_BASE + "/", {
    type: "domain_organic_organic",
    key: params.apiKey,
    domain: params.target,
    database: params.database,
    export_columns: params.exportColumns.join(","),
    export_decode: 1,
    display_limit: effectiveLimit,
    display_offset: params.displayOffset,
    display_date: params.displayDate,
    display_sort: params.displaySort,
  });
  const csv = await getSemrushCsv(url, params.apiKey, params.exportColumns);
  if (!csv.success) return csv;
  const perRow = params.displayDate ? 200 : 40;
  return adapterOk({
    result: csv.data,
    estimatedUnits: perRow * csv.data.rows.length,
  });
}

// ============================================================================
// Story 35.1 — Backlinks expansion (AC #2, #3, #4)
//
// Seven NEW helpers covering the full Backlinks API surface:
//   - fetchSemrushBacklinks            (link-list, 15 default cols)
//   - fetchSemrushBacklinksRefdomains  (referring domains, 8 default cols)
//   - fetchSemrushBacklinksAnchors     (anchor aggregates, 5 default cols)
//   - fetchSemrushBacklinksPages       (page aggregates, 4 default cols)
//   - fetchSemrushBacklinksTld         (TLD aggregates, 3 default cols)
//   - fetchSemrushBacklinksGeo         (geo aggregates, 3 default cols)
//   - fetchSemrushBacklinksRefips      (refIp aggregates, 4 default cols)
//
// All seven endpoints share:
//   - Base URL: `SEMRUSH_BACKLINKS_API_BASE` (analytics/v1, NOT analytics root)
//   - Fixed per-call cost: 40 units regardless of row count (per
//     developer.semrush.com/api/v3/analytics/backlinks/ verified 2026-05-19).
//   - targetType ∈ {"root_domain", "domain", "url"} — explicit consent
//     required (different scopes return dramatically different row counts).
//   - displayLimit clamp 1..1000 (carry-forward Story 20.4 invariant).
//
// The 5 aggregate endpoints (anchors/pages/tld/geo/refips) share a private
// `fetchSemrushBacklinksAggregate<TReportType>` factory because their wire
// shapes differ only in (endpoint name, default columns) — mirrors the
// Story 20.5 `fetchSemrushPhraseExpansion` pattern.
// ============================================================================

/**
 * `backlinks` default export_columns (15 fields) per Story 35.1 AC #2.
 *
 * Headline columns the GreenLife dashboard renders. Remaining 7 catalogue
 * fields (`external_num`, `internal_num`, `redirect_url`, `image_url`,
 * `image_alt`, `source_size`, `last_seen`) are widget-config opt-ins
 * (allowed by the metric-allowlist but not in the default).
 */
export const SEMRUSH_BACKLINKS_DEFAULT_COLUMNS: ReadonlyArray<string> = [
  "page_ascore",
  "response_code",
  "source_url",
  "source_title",
  "target_url",
  "target_title",
  "anchor",
  "nofollow",
  "form",
  "frame",
  "image",
  "sitewide",
  "newlink",
  "lostlink",
  "first_seen",
];

/** `backlinks_refdomains` default columns (8 fields) per AC #3. */
export const SEMRUSH_BACKLINKS_REFDOMAINS_DEFAULT_COLUMNS: ReadonlyArray<string> = [
  "domain_ascore",
  "domain",
  "backlinks_num",
  "domain_trust_score",
  "ip",
  "country",
  "first_seen",
  "last_seen",
];

/** `backlinks_anchors` default columns (5 fields) per AC #4. */
export const SEMRUSH_BACKLINKS_ANCHORS_DEFAULT_COLUMNS: ReadonlyArray<string> = [
  "anchor",
  "domains_num",
  "backlinks_num",
  "first_seen",
  "last_seen",
];

/** `backlinks_pages` default columns (4 fields) per AC #4. */
export const SEMRUSH_BACKLINKS_PAGES_DEFAULT_COLUMNS: ReadonlyArray<string> = [
  "source_url",
  "backlinks_num",
  "domains_num",
  "response_code",
];

/** `backlinks_tld` default columns (3 fields) per AC #4. */
export const SEMRUSH_BACKLINKS_TLD_DEFAULT_COLUMNS: ReadonlyArray<string> = [
  "zone",
  "domains_num",
  "backlinks_num",
];

/** `backlinks_geo` default columns (3 fields) per AC #4. */
export const SEMRUSH_BACKLINKS_GEO_DEFAULT_COLUMNS: ReadonlyArray<string> = [
  "country",
  "domains_num",
  "backlinks_num",
];

/** `backlinks_refips` default columns (4 fields) per AC #4. */
export const SEMRUSH_BACKLINKS_REFIPS_DEFAULT_COLUMNS: ReadonlyArray<string> = [
  "ip",
  "country",
  "domains_num",
  "backlinks_num",
];

/**
 * Backlinks endpoints all bill **40 units FIXED per request** per
 * developer.semrush.com/api/v3/analytics/backlinks/ verified 2026-05-19.
 * Defined as a module constant so tests can assert the contract and a
 * single edit propagates to all 7 helpers.
 */
export const SEMRUSH_BACKLINKS_UNIT_COST = 40;

/**
 * Round 3 CR MEDIUM #3: conservative default row clamp for Backlinks
 * endpoints. Unit cost is FIXED (40u/call regardless of row count) so this
 * isn't a budget defense — it's a PAYLOAD-SIZE defense. Without a default,
 * a missing `displayLimit` falls through to Semrush's implicit row cap and
 * the response could be 10K+ rows × 14 fields = ~2 MB of JSON that we then
 * (a) transform, (b) JSON.stringify into KV cache, (c) ship as the response
 * body. The KV cache cost is fine (25 MB ceiling), but browser-side render
 * performance suffers and the cache key collision space grows
 * cardinality-wise. 500 rows × 14 fields ≈ 100 KB transformed payload.
 */
export const SEMRUSH_BACKLINKS_DEFAULT_LIMIT = 500;

// ----------------------------------------------------------------------------
// fetchSemrushBacklinks (AC #2)
// ----------------------------------------------------------------------------

export interface SemrushBacklinksParams {
  readonly apiKey: string;
  readonly target: string;
  readonly targetType: "root_domain" | "domain" | "url";
  readonly exportColumns: ReadonlyArray<string>;
  readonly displayLimit?: number;
  readonly displayOffset?: number;
  readonly displaySort?: string;
  readonly displayFilter?: string;
}

/**
 * `GET /analytics/v1/?type=backlinks` — full backlinks link-list with new /
 * lost flags, anchor text, response codes, and link-type indicators.
 *
 * Cost: **40 units FIXED per request** (NOT per row). The Backlinks API bills
 * the call shape regardless of how many rows come back — a 0-row response
 * costs the same as a 1000-row one.
 *
 * TODO(35.2): fixture mode short-circuit — when the future Story 35.2 fixture
 * expansion lands, intercept here BEFORE `safeFetchSemrush` (mirrors the
 * Story 20.5 / 20.6 pattern on `fetchSemrushDomainOrganic` above).
 */
export async function fetchSemrushBacklinks(
  params: SemrushBacklinksParams
): Promise<AdapterResult<SemrushHelperResult>> {
  assertExportColumnsNonEmpty(params.exportColumns, "/backlinks");
  assertDisplayLimitWithinCeiling(params.displayLimit, "/backlinks");
  assertTargetType(params.targetType, "/backlinks");
  const url = buildUrl(SEMRUSH_BACKLINKS_API_BASE + "/", {
    type: "backlinks",
    key: params.apiKey,
    target: params.target,
    target_type: params.targetType,
    export_columns: params.exportColumns.join(","),
    export_decode: 1,
    display_limit: params.displayLimit,
    display_offset: params.displayOffset,
    display_sort: params.displaySort,
    display_filter: params.displayFilter,
  });
  const csv = await getSemrushCsv(url, params.apiKey, params.exportColumns);
  if (!csv.success) return csv;
  return adapterOk({
    result: csv.data,
    estimatedUnits: SEMRUSH_BACKLINKS_UNIT_COST,
  });
}

// ----------------------------------------------------------------------------
// fetchSemrushBacklinksRefdomains (AC #3)
// ----------------------------------------------------------------------------

export interface SemrushBacklinksRefdomainsParams {
  readonly apiKey: string;
  readonly target: string;
  readonly targetType: "root_domain" | "domain" | "url";
  readonly exportColumns: ReadonlyArray<string>;
  readonly displayLimit?: number;
  readonly displayOffset?: number;
  readonly displaySort?: string;
  readonly displayFilter?: string;
}

/**
 * `GET /analytics/v1/?type=backlinks_refdomains` — referring-domain breakdown
 * with Authority Score, Trust Score, IP, country, first/last seen.
 *
 * Cost: 40 units FIXED per request (same model as `/backlinks`).
 *
 * TODO(35.2): fixture mode short-circuit.
 */
export async function fetchSemrushBacklinksRefdomains(
  params: SemrushBacklinksRefdomainsParams
): Promise<AdapterResult<SemrushHelperResult>> {
  assertExportColumnsNonEmpty(params.exportColumns, "/backlinks_refdomains");
  assertDisplayLimitWithinCeiling(params.displayLimit, "/backlinks_refdomains");
  assertTargetType(params.targetType, "/backlinks_refdomains");
  const url = buildUrl(SEMRUSH_BACKLINKS_API_BASE + "/", {
    type: "backlinks_refdomains",
    key: params.apiKey,
    target: params.target,
    target_type: params.targetType,
    export_columns: params.exportColumns.join(","),
    export_decode: 1,
    display_limit: params.displayLimit,
    display_offset: params.displayOffset,
    display_sort: params.displaySort,
    display_filter: params.displayFilter,
  });
  const csv = await getSemrushCsv(url, params.apiKey, params.exportColumns);
  if (!csv.success) return csv;
  return adapterOk({
    result: csv.data,
    estimatedUnits: SEMRUSH_BACKLINKS_UNIT_COST,
  });
}

// ----------------------------------------------------------------------------
// Aggregate-endpoint factory (AC #4)
//
// Five Backlinks aggregate endpoints (anchors / pages / tld / geo / refips)
// differ only in their endpoint name + default columns + accepted params.
// None of them accept `display_filter` (per docs verified 2026-05-19 —
// aggregation endpoints reject filter expressions).
// ----------------------------------------------------------------------------

export interface SemrushBacklinksAggregateParams {
  readonly apiKey: string;
  readonly target: string;
  readonly targetType: "root_domain" | "domain" | "url";
  readonly exportColumns: ReadonlyArray<string>;
  readonly displayLimit?: number;
  readonly displayOffset?: number;
  readonly displaySort?: string;
}

type SemrushBacklinksAggregateReportType =
  | "backlinks_anchors"
  | "backlinks_pages"
  | "backlinks_tld"
  | "backlinks_geo"
  | "backlinks_refips";

async function fetchSemrushBacklinksAggregate(
  reportType: SemrushBacklinksAggregateReportType,
  params: SemrushBacklinksAggregateParams
): Promise<AdapterResult<SemrushHelperResult>> {
  const endpoint = `/${reportType}`;
  assertExportColumnsNonEmpty(params.exportColumns, endpoint);
  assertDisplayLimitWithinCeiling(params.displayLimit, endpoint);
  assertTargetType(params.targetType, endpoint);
  const url = buildUrl(SEMRUSH_BACKLINKS_API_BASE + "/", {
    type: reportType,
    key: params.apiKey,
    target: params.target,
    target_type: params.targetType,
    export_columns: params.exportColumns.join(","),
    export_decode: 1,
    display_limit: params.displayLimit,
    display_offset: params.displayOffset,
    display_sort: params.displaySort,
  });
  const csv = await getSemrushCsv(url, params.apiKey, params.exportColumns);
  if (!csv.success) return csv;
  return adapterOk({
    result: csv.data,
    estimatedUnits: SEMRUSH_BACKLINKS_UNIT_COST,
  });
}

/**
 * `backlinks_anchors` — anchor-text distribution. Cost: 40u FIXED per request.
 * TODO(35.2): fixture mode short-circuit.
 */
export function fetchSemrushBacklinksAnchors(
  params: SemrushBacklinksAggregateParams
): Promise<AdapterResult<SemrushHelperResult>> {
  return fetchSemrushBacklinksAggregate("backlinks_anchors", params);
}

/**
 * `backlinks_pages` — page-level backlinks distribution. 40u FIXED.
 * TODO(35.2): fixture mode short-circuit.
 */
export function fetchSemrushBacklinksPages(
  params: SemrushBacklinksAggregateParams
): Promise<AdapterResult<SemrushHelperResult>> {
  return fetchSemrushBacklinksAggregate("backlinks_pages", params);
}

/**
 * `backlinks_tld` — TLD distribution. 40u FIXED.
 * TODO(35.2): fixture mode short-circuit.
 */
export function fetchSemrushBacklinksTld(
  params: SemrushBacklinksAggregateParams
): Promise<AdapterResult<SemrushHelperResult>> {
  return fetchSemrushBacklinksAggregate("backlinks_tld", params);
}

/**
 * `backlinks_geo` — country distribution. 40u FIXED.
 * TODO(35.2): fixture mode short-circuit.
 */
export function fetchSemrushBacklinksGeo(
  params: SemrushBacklinksAggregateParams
): Promise<AdapterResult<SemrushHelperResult>> {
  return fetchSemrushBacklinksAggregate("backlinks_geo", params);
}

/**
 * `backlinks_refips` — referring-IP distribution. 40u FIXED.
 * TODO(35.2): fixture mode short-circuit.
 */
export function fetchSemrushBacklinksRefips(
  params: SemrushBacklinksAggregateParams
): Promise<AdapterResult<SemrushHelperResult>> {
  return fetchSemrushBacklinksAggregate("backlinks_refips", params);
}

// ============================================================================
// Story 35.1 — URL Reports (AC #5)
//
// Per-URL keyword reports: `url_organic` (organic keywords ranking for the
// URL) and `url_adwords` (paid keywords). Differ from `domain_organic` /
// `domain_adwords` in that the discriminator is `url=<fully-qualified-URL>`
// instead of `domain=<bare-host>`.
//
// Cost (per developer.semrush.com/api/v3/analytics/domain-reports/ verified
// 2026-05-19):
//   - url_organic: 10u/row LIVE, 50u/row HISTORICAL
//   - url_adwords: 20u/row LIVE, 100u/row HISTORICAL
// ============================================================================

/**
 * `url_organic` default export_columns (11 fields) per AC #5.
 *
 * Same as `domain_organic` MINUS `Fk` — URL-level reports historically don't
 * surface SERP-feature codes consistently. If Semrush adds them, opt-in via
 * widget config.
 */
export const SEMRUSH_URL_ORGANIC_DEFAULT_COLUMNS: ReadonlyArray<string> = [
  "Ph",
  "Po",
  "Pp",
  "Pd",
  "Nq",
  "Cp",
  "Ur",
  "Tr",
  "Tc",
  "Co",
  "Td",
];

/**
 * `url_adwords` default export_columns (8 fields) per AC #5. Includes `Vu`
 * (visible URL) because ad copy is keyword-scoped per URL.
 */
export const SEMRUSH_URL_ADWORDS_DEFAULT_COLUMNS: ReadonlyArray<string> = [
  "Ph",
  "Po",
  "Nq",
  "Cp",
  "Vu",
  "Tr",
  "Tc",
  "Co",
];

/**
 * Story 35.1 Round 1 Code Review HIGH #1: conservative-default row clamp
 * for `url_organic` / `url_adwords`. Without this, a widget config that
 * omits `displayLimit` falls through to Semrush's implicit default (~10K
 * rows), which costs ~100K units LIVE or ~500K HISTORICAL for a single call
 * — enough to single-handedly blow the 200K/day per-tenant budget. The
 * proxy route applies this default when the caller didn't pass an explicit
 * limit; widget configs can override up to `SEMRUSH_DISPLAY_LIMIT_MAX` (1000).
 *
 * 100 rows × 10u LIVE = 1000u/call (well below budget).
 * 100 rows × 50u HISTORICAL = 5000u/call.
 */
export const SEMRUSH_URL_REPORTS_DEFAULT_LIMIT = 100;

/**
 * URL reports require a fully-qualified URL (NOT a bare domain) per
 * developer.semrush.com/api/v3/analytics/domain-reports/ verified 2026-05-19
 * ("Website URL to investigate" required parameter).
 */
function assertUrl(url: string, endpoint: string): void {
  if (typeof url !== "string" || url.length === 0) {
    throw new TypeError(`Semrush ${endpoint}: url must be a non-empty string.`);
  }
  if (!/^https?:\/\//i.test(url)) {
    throw new TypeError(
      `Semrush ${endpoint}: url must start with http:// or https:// (URL reports require a fully-qualified URL, NOT a bare domain).`
    );
  }
}

export interface SemrushUrlReportParams {
  readonly apiKey: string;
  readonly url: string;
  readonly database: string;
  readonly exportColumns: ReadonlyArray<string>;
  readonly displayLimit?: number;
  readonly displayOffset?: number;
  readonly displayDate?: string; // YYYYMM15 for historical
  readonly displaySort?: string;
  readonly displayFilter?: string;
}

/**
 * `GET /?type=url_organic` — every organic keyword the given URL ranks for.
 *
 * Cost: `displayDate ? 50 * rows : 10 * rows`.
 *
 * TODO(35.2): fixture mode short-circuit.
 */
export async function fetchSemrushUrlOrganic(
  params: SemrushUrlReportParams
): Promise<AdapterResult<SemrushHelperResult>> {
  assertExportColumnsNonEmpty(params.exportColumns, "/url_organic");
  assertDisplayLimitWithinCeiling(params.displayLimit, "/url_organic");
  assertDatabase(params.database, "/url_organic");
  assertUrl(params.url, "/url_organic");
  // Round 1 CR MEDIUM #4: validate displayDate format (must be YYYYMM15).
  // Semrush rejects other formats opaquely; surface as synchronous TypeError
  // so widget-config bugs fail at dev time, not at unit-budget burn time.
  if (params.displayDate !== undefined) {
    assertYyyyMm15(params.displayDate, "/url_organic");
  }
  const builtUrl = buildUrl(SEMRUSH_ANALYTICS_API_BASE + "/", {
    type: "url_organic",
    key: params.apiKey,
    url: params.url,
    database: params.database,
    export_columns: params.exportColumns.join(","),
    export_decode: 1,
    display_limit: params.displayLimit,
    display_offset: params.displayOffset,
    display_date: params.displayDate,
    display_sort: params.displaySort,
    display_filter: params.displayFilter,
  });
  const csv = await getSemrushCsv(builtUrl, params.apiKey, params.exportColumns);
  if (!csv.success) return csv;
  const perRow = params.displayDate ? 50 : 10;
  return adapterOk({
    result: csv.data,
    estimatedUnits: perRow * csv.data.rows.length,
  });
}

/**
 * `GET /?type=url_adwords` — every paid keyword the given URL is bid on for,
 * with visible-URL + ad-copy-equivalent fields.
 *
 * Cost: `displayDate ? 100 * rows : 20 * rows`.
 *
 * TODO(35.2): fixture mode short-circuit.
 */
export async function fetchSemrushUrlAdwords(
  params: SemrushUrlReportParams
): Promise<AdapterResult<SemrushHelperResult>> {
  assertExportColumnsNonEmpty(params.exportColumns, "/url_adwords");
  assertDisplayLimitWithinCeiling(params.displayLimit, "/url_adwords");
  assertDatabase(params.database, "/url_adwords");
  assertUrl(params.url, "/url_adwords");
  // Round 1 CR MEDIUM #4: validate displayDate format.
  if (params.displayDate !== undefined) {
    assertYyyyMm15(params.displayDate, "/url_adwords");
  }
  const builtUrl = buildUrl(SEMRUSH_ANALYTICS_API_BASE + "/", {
    type: "url_adwords",
    key: params.apiKey,
    url: params.url,
    database: params.database,
    export_columns: params.exportColumns.join(","),
    export_decode: 1,
    display_limit: params.displayLimit,
    display_offset: params.displayOffset,
    display_date: params.displayDate,
    display_sort: params.displaySort,
    display_filter: params.displayFilter,
  });
  const csv = await getSemrushCsv(builtUrl, params.apiKey, params.exportColumns);
  if (!csv.success) return csv;
  const perRow = params.displayDate ? 100 : 20;
  return adapterOk({
    result: csv.data,
    estimatedUnits: perRow * csv.data.rows.length,
  });
}
